<script setup lang="ts">
import { computed } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { useStaticAsset } from '@/composables/useStaticAsset'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import { currentUser } from '@/lib/currentUser'

interface Review {
  name: string
  image: string
  body: string
}

interface BaseSeminarInfoPageContext {
  seminar_id: number
  reviews: Review[]
}

const ctx = usePageContext<BaseSeminarInfoPageContext>()
const { screenWidth } = useScreen()
const asset = useStaticAsset()
const explanationBlobImage = `url('${asset('img/base-seminar-info/explanation_blob.svg')}')`

const isMobileLocal = computed(() => screenWidth.value <= 1200)

const gallerySliderConfig = {
  slidesPerView: 'auto',
  spaceBetween: 16,
  watchOverflow: true
}

const reviewsSliderConfig = {
  slidesPerView: 'auto',
  spaceBetween: 16,
  watchOverflow: true,
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev'
  }
}

const registerLink = computed(() => {
  if (currentUser.id === 0) {
    return `/users/login?tab=register&type=teacher&cert_step=false&purpose=seminar&seminar_id=${ctx.seminar_id}&next=/seminars/purchase_seminar/${ctx.seminar_id}`
  }

  return `/seminars/purchase_seminar/${ctx.seminar_id}`
})

const descriptionLink = computed(() => `/seminars/seminar_description/${ctx.seminar_id}`)
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" rules-url="/website/documents/oferta" />

    <div v-cloak class="content base-seminar-info">
      <section class="base-seminar-hero">
        <img class="base-seminar-hero__bg-1" :src="asset('img/base-seminar-info/hero_bg_1.svg')" />
        <img class="base-seminar-hero__bg-2" :src="asset('img/base-seminar-info/hero_bg_2.svg')" />

        <p class="base-seminar-hero__small-caption">Авторский онлайн-семинар Валерии Мещеряковой</p>
        <h1 class="base-seminar-hero__heading">Базовый курс методики обучения детей 2-10 лет английскому языку</h1>
        <p class="base-seminar-hero__caption">
          Игровая методика Валерии Мещеряковой - это путь, который позволит по-новому достигать результатов в обучении
          детей английскому языку!
        </p>

        <div class="base-seminar-hero__buttons">
          <v-button mode="primary filled large rounded" :href="registerLink"> Купить </v-button>

          <v-button mode="primary outlined large rounded" :href="descriptionLink"> Подробнее о семинаре </v-button>
        </div>

        <img v-if="!isMobileLocal" :src="asset('img/base-seminar-info/hero_imgs.svg')" />
        <v-slider v-else class="base-seminar-hero__gallery mt-1" :config="gallerySliderConfig">
          <div class="swiper-slide">
            <img class="base-seminar-hero__gallery-img" :src="asset('img/base-seminar-info/hero_img_1.jpg')" />
          </div>
          <div class="swiper-slide">
            <img class="base-seminar-hero__gallery-img" :src="asset('img/base-seminar-info/hero_img_2.jpg')" />
          </div>
          <div class="swiper-slide">
            <img class="base-seminar-hero__gallery-img" :src="asset('img/base-seminar-info/hero_img_3.jpg')" />
          </div>
          <div class="swiper-slide">
            <img class="base-seminar-hero__gallery-img" :src="asset('img/base-seminar-info/hero_img_4.jpg')" />
          </div>
        </v-slider>
      </section>

      <section class="base-seminar-register">
        <div class="base-seminar-register__main-row">
          <h2 class="base-seminar-register__title">Регистрируйтесь на семинар и получите ясную пошаговую Систему</h2>

          <p class="base-seminar-register__caption mt-2">
            которая даст вам уверенность в том, что ваши уроки всегда интересны и дети будут говорить без языкового
            барьера!
          </p>

          <div v-if="isMobileLocal" class="flex-spacer mt-4" />
          <div v-else class="flex-spacer" />

          <div class="base-seminar-register__buttons">
            <v-button
              :mode="isMobileLocal ? 'primary filled large rounded block' : 'primary filled large rounded'"
              :href="registerLink"
            >
              Зарегистрироваться
              <v-icon class="ml-1">mdi-arrow-right</v-icon>
            </v-button>
            <v-button
              :mode="isMobileLocal ? 'primary outlined large rounded block' : 'primary outlined large rounded'"
              :href="descriptionLink"
            >
              Подробнее о семинаре
            </v-button>
          </div>
        </div>

        <div class="base-seminar-register__card-row mt-6 mt-d-0">
          <span>Более</span>
          <b class="blue-text">3400 учителей</b>
          <b class="orange-text">в 130+ городах</b>
          <span>России и стран СНГ</span>
          <span class="mt-1">работают по Системе Валерии Мещеряковой</span>
        </div>
      </section>

      <section class="base-seminar-explanation">
        <header class="base-seminar-explanation__header">
          <h2>
            За годы работы команда нашего центра пришла к интересному выводу
            <img
              v-if="!isMobileLocal"
              class="base-seminar-explanation__header-arrow"
              :src="asset('img/base-seminar-info/explanation_arrow.svg')"
            />
          </h2>

          <p class="base-seminar-explanation__header-blob">
            Самые неудовлетворительные результаты от уроков получают именно те учителя, которые работают на износ и по
            несколько часов в день готовятся к предстоящему уроку.
          </p>
        </header>

        <div class="base-seminar-explanation__content">
          <img class="base-seminar-explanation__img" :src="asset('img/base-seminar-info/explanation_img.svg')" />

          <div class="base-seminar-explanation__content-body">
            <h3>Почему?</h3>
            <p class="mt-2">
              Дело в том, что они упускают одну важную вещь.<br />
              <b class="black-text">Детское внимание очень легко рассеивается.</b>
            </p>
            <p class="mt-3">
              И единственное, что может закрепить новые знания у детей и привести их к удивительным результатам - это
              <b class="blue-text">СИСТЕМНОСТЬ</b>, с которой ведутся уроки в течение всего периода обучения, и которая
              учитывает детское восприятие информации.
            </p>
            <p class="mt-2">
              Обычно бывает, что учитель тратит массу времени на то, чтобы придумать план урока, собрать материалы и
              провести его, но забывает о том, <b>как воспринимают информацию дети.</b>
            </p>
            <p class="mt-3">
              <b class="black-text">Такая система существует уже более 30 лет, изучите ее!</b>
            </p>
          </div>
        </div>
      </section>

      <section class="base-seminar-author">
        <img class="base-seminar-author__img" :src="asset('img/base-seminar-info/author_img.jpg')" />

        <div class="base-seminar-author__content">
          <p><small>Автор методики</small></p>
          <h2 class="mt-2">Валерия Мещерякова</h2>
          <p class="mt-3">
            Создала систему естественного обучения детей английскому языку через игру, которая делает процесс обучения
            незаметным.
          </p>
          <p class="mt-2">
            Ребенок приходит на урок НЕ учить английский, а играть на английском, поэтому начинает общаться с учителем и
            друзьями с первых уроков: ему интересно и все понятно. Здесь он в своей тарелке.
          </p>
        </div>
      </section>

      <section class="base-seminar-efficiency">
        <header class="base-seminar-efficiency__header">
          <h2>
            В чем заключается эффективность<br />
            <span class="blue-text">I LOVE ENGLISH?</span>
          </h2>
          <p class="mt-3">
            Главная особенность - игровое обучение с погружением в языковую среду и большой упор на аудирование!
          </p>
          <p class="mt-1">
            Детям будет интересно учиться, ведь они играют, поют и разыгрывают сценки, таким образом, остаются в
            привычной для себя среде.
          </p>
        </header>

        <ul class="base-seminar-efficiency__grid">
          <li
            class="base-seminar-efficiency__grid-item base-seminar-efficiency__grid-item_row_1"
            :style="{ '--card_bg': '#F2F8FE', '--icon_bg': '#E0EDFF' }"
          >
            <div class="base-seminar-efficiency__grid-item-icon">
              <img :src="asset('img/base-seminar-info/efficiency_grid_chat.svg')" />
            </div>
            <div class="base-seminar-efficiency__grid-item-content">
              <h3>Освоение языковой среды</h3>
              <p class="mt-2">
                Мы начинаем изучение языков не с правил и изучения лексики,
                <b>а с аудирования и общения с первых уроков!</b>
              </p>
            </div>
          </li>
          <li
            class="base-seminar-efficiency__grid-item base-seminar-efficiency__grid-item_row_1"
            :style="{ '--card_bg': '#FFF5F2', '--icon_bg': '#FFEFEB' }"
          >
            <div class="base-seminar-efficiency__grid-item-icon">
              <img :src="asset('img/base-seminar-info/efficiency_grid_speech.svg')" />
            </div>
            <div class="base-seminar-efficiency__grid-item-content">
              <h3>Внимание к речевым ошибкам</h3>
              <p class="mt-2">
                Постоянное повторение материала и на уроках, и дома, позволит детям освоить грамотный вариант речи, а
                правильное и своевременное реагирование учителя на ошибки на уроках
                <b>предотвратит появление языкового барьера!</b>
              </p>
            </div>
          </li>
          <li
            class="base-seminar-efficiency__grid-item base-seminar-efficiency__grid-item_row_2"
            :style="{ '--card_bg': '#FFF7EB', '--icon_bg': '#FDECD8' }"
          >
            <div class="base-seminar-efficiency__grid-item-icon">
              <img :src="asset('img/base-seminar-info/efficiency_grid_ear.svg')" />
            </div>
            <div class="base-seminar-efficiency__grid-item-content">
              <h3>Слуховое восприятие языка</h3>
              <p class="mt-2">
                С помощью игр, пения и сценок дети проще воспринимают новую информацию!
                <b>А домашнее прослушивание комиксов дает огромную базу для взаимопонимания на уроках</b>
              </p>
            </div>
          </li>
          <li
            class="base-seminar-efficiency__grid-item base-seminar-efficiency__grid-item_row_2"
            :style="{ '--card_bg': '#F7F5FE', '--icon_bg': '#EEEBFF' }"
          >
            <div class="base-seminar-efficiency__grid-item-icon">
              <img :src="asset('img/base-seminar-info/efficiency_grid_music.svg')" />
            </div>
            <div class="base-seminar-efficiency__grid-item-content">
              <h3>Интерактивное домашнее задание</h3>
              <p class="mt-2">
                <b>Авторские 10-15 минутные аудиоуроки</b> от носителей языка закладывают большой языковой пласт знаний
                у детей, а также естественным образом подведут их к правильному произношению!
              </p>
            </div>
          </li>

          <div v-if="!isMobileLocal" class="base-seminar-efficiency__grid-button">
            <v-button mode="primary filled xlarge rounded" :href="descriptionLink">Подробнее о семинаре</v-button>
          </div>
        </ul>
      </section>

      <section class="base-seminar-timeline">
        <object
          v-if="isMobileLocal"
          type="image/svg+xml"
          :data="asset('img/base-seminar-info/timeline_chart_mobile.svg')"
          class="pb-5"
        />
        <object v-else type="image/svg+xml" :data="asset('img/base-seminar-info/timeline_chart_desktop.svg')" />

        <p class="base-seminar-timeline__text base-seminar-timeline__text_top">
          На создание, проверку каждого приема в обучении и оттачивание Системы ушло
          <b class="mt-2">более 10 лет</b>
        </p>

        <p class="base-seminar-timeline__text base-seminar-timeline__text_bottom">
          На ее улучшения с опорой на опыт применения методики -
          <b class="mt-2">более 3400 педагогами - более 20 лет</b>
        </p>
      </section>

      <section class="base-seminar-instruments">
        <header class="base-seminar-instruments__header">
          <p>И сегодня перед вами</p>
          <h2 class="mt-3">Полный комплект готовых инструментов</h2>
          <p class="mt-2">которые можно взять и начать использовать сразу после прохождения семинара по методике</p>
        </header>

        <div class="base-seminar-instruments__bentogrid">
          <div
            class="base-seminar-instruments__bentogrid-item"
            :style="{ '--card_bg': '#F2F8FE', 'grid-row': '1 / 5' }"
          >
            <h3>Книги для учителя</h3>
            <p>содержат подробные планы уроков из 10-15 видов игровой деятельности с чередованием каждые 2-3 минуты</p>
            <img :src="asset('img/base-seminar-info/instruments_teacher_book.jpg')" />
          </div>

          <div
            class="base-seminar-instruments__bentogrid-item"
            :style="{ '--card_bg': '#FFF7EB', 'grid-row': '1 / 4' }"
          >
            <h3>Аудиоуроки</h3>
            <p>для домашней работы</p>
            <img :src="asset('img/base-seminar-info/instruments_audio.jpg')" />
          </div>

          <div class="base-seminar-instruments__bentogrid-item" :style="{ '--card_bg': '#F7F5FE' }">
            <h3>Заметки для родителей</h3>
            <p>с пояснениями и рекомендациями от автора</p>
          </div>

          <div
            class="base-seminar-instruments__bentogrid-item"
            :style="{ '--card_bg': '#EEF9E6', 'grid-row': '4 / 7' }"
          >
            <h3>Наглядные пособия</h3>
            <p>для введения и отработки материала на уроке</p>
            <img :src="asset('img/base-seminar-info/instruments_book.jpg')" />
          </div>

          <div class="base-seminar-instruments__bentogrid-item" :style="{ '--card_bg': '#F7F5FE' }">
            <h3>Методическая поддержка</h3>
            <p>коллег в чате и на программах повышения квалификации для практикующих учителей</p>
          </div>
        </div>
      </section>

      <section class="base-seminar-keypoints">
        <header class="base-seminar-keypoints__header">
          <p>
            Только представьте - все это в комплексе, вместе с применяемыми методическими принципами, которые даются на
            семинаре -
          </p>
          <img alt="Залог высоких результатов обучения" :src="asset('img/base-seminar-info/keypoints_header.svg')" />
        </header>

        <ul class="base-seminar-keypoints__list">
          <li class="base-seminar-keypoints__list-item base-seminar-keypoints__list-item_marker_blue">
            Дети понимают Вас с первых уроков<br />
            <small>и активно взаимодействуют с вами</small>
          </li>
          <li class="base-seminar-keypoints__list-item base-seminar-keypoints__list-item_marker_orange">
            Не нужно искать что-то дополнительно<br />
            <small>только внимательно применять полученные инструменты обучения, пропуская их через себя</small>
          </li>
          <li class="base-seminar-keypoints__list-item base-seminar-keypoints__list-item_marker_red">
            Родителям необязательно помогать детям<br />
            <small
              >только приучить к ежедневному прослушиванию аудио, постепенно усложняющиеся задания к ним ребенок может
              выполнить сам</small
            >
          </li>
        </ul>

        <div class="base-seminar-keypoints__buttons">
          <v-button mode="primary filled xlarge rounded" :href="registerLink">Купить</v-button>
          <v-button mode="primary outlined xlarge rounded" :href="descriptionLink">Подробнее о семинаре</v-button>
        </div>
      </section>

      <section class="base-seminar-reviews">
        <h2 v-if="isMobileLocal" class="base-seminar-reviews__title">Отзывы</h2>
        <h2 v-else class="base-seminar-reviews__title">Отзывы о семинаре</h2>

        <v-slider :config="reviewsSliderConfig">
          <article v-for="review in ctx.reviews" :key="review.name" class="swiper-slide base-seminar-reviews__slide">
            <div class="base-seminar-reviews__author-info">
              <img class="base-seminar-reviews__img" :src="review.image" />
              <h4 v-text="review.name" />
            </div>

            <p v-text="review.body" />
          </article>
        </v-slider>
      </section>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/seminars/base_seminar_info.scss' as *;

.base-seminar-explanation__header-blob {
  background-image: v-bind(explanationBlobImage);
}
</style>
